import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaHeadset, FaIdCard, FaExclamationTriangle } from 'react-icons/fa';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton } from 'Core/components/Button';
import CreateApplication from 'Modules/patientfi/routes/CreateApplication';
import PhoneCard from 'Modules/patientfi/components/PhoneCard';
import { extractErrorCodes, stripErrorCodes } from 'Modules/patientfi/utils/errorCodes';

const ACCOUNT_STATUS_LABELS = {
  REVIEW: ['patientfi.supportRequired.accountStatus.review', 'Under review'],
  SUSPENDED: ['patientfi.supportRequired.accountStatus.suspended', 'Suspended'],
  ON_HOLD: ['patientfi.supportRequired.accountStatus.onHold', 'On hold'],
  EXPIRED: ['patientfi.supportRequired.accountStatus.expired', 'Expired'],
  INCOMPLETE: ['patientfi.supportRequired.accountStatus.incomplete', 'Incomplete'],
};

const REASON_DESCRIPTIONS = {
  ACCOUNT_SUSPENDED: [
    'patientfi.supportRequired.reason.accountSuspended',
    'PatientFi has suspended your account, so it cannot be used for this purchase until the suspension is resolved.',
  ],
  ACCOUNT_ON_HOLD: [
    'patientfi.supportRequired.reason.accountOnHold',
    'PatientFi has placed a hold on your account, so it cannot be used for this purchase until the hold is removed.',
  ],
  ACCOUNT_IN_REVIEW: [
    'patientfi.supportRequired.reason.accountInReview',
    'PatientFi is currently reviewing your account, so it cannot be used for this purchase until the review is complete.',
  ],
  ACCOUNT_INCOMPLETE: [
    'patientfi.supportRequired.reason.accountIncomplete',
    'PatientFi needs more information to complete your account before it can be used for this purchase.',
  ],
  ACCOUNT_LINKED_TO_OTHER_CUSTOMER: [
    'patientfi.supportRequired.reason.accountLinkedToOtherCustomer',
    'Your PatientFi account is already linked to another customer record with us, so it cannot be used for this purchase until this is resolved.',
  ],
  ACCOUNT_NEEDS_SUPPORT: [
    'patientfi.supportRequired.reason.accountNeedsSupport',
    'We could not continue with your PatientFi account, so it cannot be used for this purchase until PatientFi support has resolved the issue.',
  ],
};

const StepHeading = ({ number, children }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
      {number}
    </span>
    <span className="font-medium text-gray-700">{children}</span>
  </div>
);

const InfoTile = ({ Icon, iconClassName, title, description, 'data-testid': dataTestId }) => (
  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
    <div className="flex items-center gap-3">
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${iconClassName}`}
      >
        <Icon className="text-lg" />
      </div>
      <div>
        <p className="font-medium text-gray-900" data-testid={`${dataTestId}-title`}>
          {title}
        </p>
        {description && (
          <p className="text-sm text-gray-500 mt-0.5" data-testid={dataTestId}>
            {description}
          </p>
        )}
      </div>
    </div>
  </div>
);

export default function FinancingSupportRequired({ customer_id, application = {} }) {
  const { t } = useTranslation();
  document.title = t('patientfi.supportRequired.pageTitle', 'Payment Portal - Contact PatientFi');
  useScrollTop();
  const [refreshedFrom, setRefreshedFrom] = React.useState(null);
  const refresh = refreshedFrom === application;

  const {
    payload: { correction_required: { message = '' } = {}, existing_account: existingAccount } = {},
  } = application;
  const accountNumber = existingAccount?.account_number;
  const recheckLabel = t(
    'patientfi.supportRequired.recheckEligibilityButton',
    'Recheck eligibility'
  );
  const accountStatusLabel = ACCOUNT_STATUS_LABELS[existingAccount?.account_status];
  const reasonDescription = REASON_DESCRIPTIONS[extractErrorCodes(message ?? '')[0]];

  if (refresh) {
    return <CreateApplication type="primary" customer_id={customer_id} purpose="create" />;
  }

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="patientfi"
    >
      <div className="max-w-4xl mx-auto mt-6">
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaHeadset className="text-white text-xl" />
              </div>
              <h1 className="text-xl font-semibold text-white" data-testid="title-support-required">
                {t('patientfi.supportRequired.title', 'Please contact PatientFi support')}
              </h1>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <StepHeading number={1}>
                {t(
                  'patientfi.supportRequired.step1Instruction',
                  'Existing PatientFi account was found'
                )}
              </StepHeading>
              <InfoTile
                Icon={FaIdCard}
                iconClassName="bg-blue-50 text-blue-600"
                title={t('patientfi.supportRequired.existingAccountTitle', 'Account Details')}
                description={
                  (accountNumber || accountStatusLabel) && (
                    <>
                      {accountNumber && (
                        <span className="block">
                          {t('patientfi.supportRequired.accountNumber', 'PatientFi account')}: ••••{' '}
                          {String(accountNumber).slice(-4)}
                        </span>
                      )}
                      {accountStatusLabel && (
                        <span className="block" data-testid="support-required-account-status">
                          {t('patientfi.supportRequired.accountStatusLabel', 'Account status')}:{' '}
                          {t(...accountStatusLabel)}
                        </span>
                      )}
                    </>
                  )
                }
                data-testid="support-required-account"
              />
            </div>

            <div className="mb-6">
              <StepHeading number={2}>
                {t('patientfi.supportRequired.step2Instruction', 'Your account status')}
              </StepHeading>
              <InfoTile
                Icon={FaExclamationTriangle}
                iconClassName="bg-amber-50 text-amber-600"
                description={reasonDescription ? t(...reasonDescription) : stripErrorCodes(message)}
                data-testid="support-required-reason"
              />
            </div>

            <div className="mb-6">
              <StepHeading number={3}>
                {t(
                  'patientfi.supportRequired.step3Instruction',
                  'Call PatientFi support to resolve it'
                )}
              </StepHeading>
              <PhoneCard
                title={t('patientfi.supportRequired.phoneTitle', 'PatientFi support')}
                phone={t('patientfi.supportPhoneNumber', '866-734-5979')}
                description={t(
                  'patientfi.supportRequired.phoneDescription',
                  'Please have your account details ready when you call.'
                )}
                data-testid="support-required-phone"
              />
            </div>

            <hr className="my-6" />

            <StepHeading number={4}>
              {t(
                'patientfi.supportRequired.step4Instruction',
                'Once PatientFi support has resolved it, Click on the {{button}} button to check updated eligibility',
                { button: recheckLabel }
              )}
            </StepHeading>

            <PrimaryButton
              className="mt-2 px-12"
              data-testid="btn-support-required-refresh"
              onClick={() => setRefreshedFrom(application)}
            >
              {recheckLabel}
            </PrimaryButton>
          </div>
        </Card>
      </div>
    </Page>
  );
}
